import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Users, Clock, Bike, Check, Mountain } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const RIDE_TYPE_LABELS = {
  allenamento: "Allenamento", lento: "Lento", recupero: "Recupero",
  ripetute: "Ripetute", cicloturismo: "Cicloturismo", gara: "Gara", social: "Social",
};

const SURFACE_LABELS = { asfaltato: "Asfalto", ghiaino: "Ghiaino", sterrato: "Sterrato", rocce: "Rocce", misto: "Misto" };
const BIKE_LABELS = { bdc: "BDC", mb: "MB", gravel: "Gravel", citybike: "City", altro: "Altro" };

const LEVEL_STYLES = {
  tutti:        { label: "Tutti", color: "#A1A1AA" },
  principiante: { label: "Principiante",   color: "#10B981" },
  intermedio:    { label: "Intermedio",      color: "#3B82F6" },
  forte:         { label: "Forte",           color: "#F59E0B" },
  pro:           { label: "Pro",             color: "#E11D48" },
};

// On the explore feed we want city/town first. Geocoder returns labels like:
//   "Via Custoza, Arzignano (VI), Veneto"     (street → city → region)
//   "Arzignano (VI), Veneto"                  (city → region)
// We swap the first two segments so the city appears first when present.
function locationLabelForCard(loc) {
  if (!loc) return "";
  const raw = loc.label || loc.name || "";
  const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 3) {
    return `${parts[1]}, ${parts[0]}, ${parts.slice(2).join(", ")}`;
  }
  return raw;
}

export default function RideCard({ ride, onUpdate }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const dt = new Date(ride.start_datetime);
  const dateStr = format(dt, "EEE d MMM", { locale: it });
  const timeStr = format(dt, "HH:mm");

  const isParticipant = !!user && (ride.participants || []).includes(user.user_id);
  const isCreator = !!user && ride.creator_id === user.user_id;

  const handleJoin = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      nav("/login");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/rides/${ride.ride_id}/join`);
      toast.success("Sei dentro!");
      onUpdate?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Link
      to={`/ride/${ride.ride_id}`}
      className="tactical-card rounded-sm p-5 block fade-up hover:-translate-y-0.5"
      data-testid={`ride-card-${ride.ride_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="label-tag mb-1">
            {RIDE_TYPE_LABELS[ride.ride_type] || ride.ride_type} · {BIKE_LABELS[ride.bike_type] || ride.bike_type}
          </div>
          <h3 className="font-display font-bold text-lg leading-tight text-gray-900">{ride.title}</h3>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-[#E11D48] text-xs uppercase tracking-wider">{dateStr}</div>
          <div className="font-display font-black text-2xl leading-none">{timeStr}</div>
          {(ride.flexibility_date_days > 0 || ride.flexibility_time_minutes > 0) && (
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-1 flex items-center justify-end gap-1">
              <Clock className="w-3 h-3" />
              ±{ride.flexibility_date_days > 0 ? `${ride.flexibility_date_days}g` : ""}
              {ride.flexibility_date_days > 0 && ride.flexibility_time_minutes > 0 ? "/" : ""}
              {ride.flexibility_time_minutes > 0 ? `${ride.flexibility_time_minutes}min` : ""}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 my-4 py-3 border-y border-gray-200">
        <div>
          <div className="label-tag">KM</div>
          <div className="font-display font-black text-xl">{ride.distance_km}</div>
        </div>
        <div>
          <div className="label-tag">D+ (M)</div>
          <div className="font-display font-black text-xl">{ride.elevation_m}</div>
        </div>
        <div>
          <div className="label-tag">Pace</div>
          <div className="font-display font-black text-xl">{ride.pace_kmh ? ride.pace_kmh : "—"}</div>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-1.5 text-gray-700 min-w-0">
          <MapPin className="w-3.5 h-3.5 text-[#E11D48] flex-shrink-0" />
          <span className="truncate max-w-[200px]" title={ride.location?.label || ride.location?.name}>{locationLabelForCard(ride.location)}</span>
          {ride.distance_from_user_km != null && (
            <span className="text-gray-500 text-xs">· {ride.distance_from_user_km} km</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          {(ride.participants_preview || []).length > 0 && (
            <div className="flex -space-x-2" data-testid="ride-card-participants-preview">
              {(ride.participants_preview || []).slice(0, 4).map((p) => (
                <div
                  key={p.user_id}
                  className="w-6 h-6 rounded-full overflow-hidden border-2 border-white bg-gray-200 flex items-center justify-center"
                  title={p.first_name}
                >
                  {p.picture ? (
                    <img src={p.picture} alt={p.first_name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[9px] font-display font-black text-gray-500 uppercase">
                      {(p.first_name || "?")[0]}
                    </span>
                  )}
                </div>
              ))}
              {ride.participants_count > 4 && (
                <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center text-[9px] font-bold text-gray-600">
                  +{ride.participants_count - 4}
                </div>
              )}
            </div>
          )}
          <div className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            <span className="font-mono">{ride.participants_count}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1.5 mt-3 flex-wrap">
        {(() => {
          const lvl = LEVEL_STYLES[ride.target_level] || LEVEL_STYLES.tutti;
          const showLvl = ride.target_level && ride.target_level !== "tutti";
          return showLvl && (
            <span className="chip" style={{ background: `${lvl.color}1F`, color: lvl.color, borderColor: `${lvl.color}66` }}>
              {lvl.label}
            </span>
          );
        })()}
        {ride.creator_power > 0 && (
          <span className="chip" style={{ background: "rgba(255,59,48,0.10)", color: "#E11D48", borderColor: "rgba(255,59,48,0.4)" }}>
            ⚡ {ride.creator_power}W
          </span>
        )}
        {(ride.surface_types || []).slice(0, 3).map((s) => (
          <span key={s} className="chip">{SURFACE_LABELS[s] || s}</span>
        ))}
      </div>

      {/* Tappe principali (waypoints) */}
      {(() => {
        const wps = (ride.waypoints || [])
          .map((w) => (typeof w === "string" ? w : (w && w.name) || ""))
          .filter(Boolean);
        if (!wps.length) return null;
        const shown = wps.slice(0, 3);
        const extra = wps.length - shown.length;
        return (
          <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid="ride-card-waypoints">
            <Mountain className="w-3 h-3 text-[#E11D48] flex-shrink-0" />
            {shown.map((name, i) => (
              <span key={`${name}-${i}`} className="text-[11px] text-gray-700 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-sm">
                {name}
              </span>
            ))}
            {extra > 0 && (
              <span className="text-[11px] text-gray-500">+{extra}</span>
            )}
          </div>
        );
      })()}

      {/* Footer with creator + quick join */}
      <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-200">
        <div className="text-xs text-gray-500 flex items-center gap-2 min-w-0">
          <span>by</span>
          <span className="text-gray-900 font-semibold truncate">{ride.creator_name}</span>
        </div>
        {isCreator ? (
          <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold flex-shrink-0">Tuo giro</span>
        ) : isParticipant ? (
          <span className="text-[10px] uppercase tracking-widest text-green-500 font-bold flex items-center gap-1 flex-shrink-0" data-testid={`ride-card-joined-${ride.ride_id}`}>
            <Check className="w-3 h-3" /> Iscritto
          </span>
        ) : (
          <button
            type="button"
            onClick={handleJoin}
            disabled={busy}
            data-testid={`ride-card-join-btn-${ride.ride_id}`}
            className="px-3 py-1.5 bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-sm text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5 disabled:opacity-50 flex-shrink-0 transition-colors"
          >
            <Bike className="w-3 h-3" /> {busy ? "..." : "Partecipa"}
          </button>
        )}
      </div>
    </Link>
  );
}
